import {
    Col,
    Divider,
    FormSection,
    List,
    Row,
    Table,
    TableBody,
    TableCell,
    TableCellExpandRow,
    TableHead,
    TableRow,
    Text,
} from '@ksyos/design-system';
import { Fragment, useState } from 'react';
import { styled } from 'styled-components';
import type { DefaultLanguage } from '@common/translate/translate';
import { getHsdqResultMessage } from '@common/utils/getHsdqResultMessage';
import { getLanguageCode, getSelectedLanguage } from '@frontend/translate';
import type { HsdqCategoryName } from '../../../../../../../common/enums/hsdqCategoryName';
import type {
    IEssOsasQuestionnaire,
    IFosq10OsasQuestionnaire,
    IHsdqOsasQuestionnaire,
    IOsasQuestionnaire,
} from '../../../../../../../common/interfaces/osasQuestionnaire';
import type { ICustomControlProcedureReadProps } from '../../../interfaces/';
import { OsasQuestionnaireName } from '../../../../../../../common/enums/osasQuestionnaireName';
import { OsasScoreColor } from '../../../../../../../common/enums/scoreColor';
import { getEssResultMessage } from '../../../../../../../common/utils/getEssResultMessage';
import {
    getHsdqAnswerByValue,
    getHsdqCategories,
    getHsdqQuestionsByCategory,
    getOsasQuestionnaireScore,
} from '../../../../../../../common/utils/osasQuestionnaireUtils';
import { FormHeader, TableHeader } from '../../../../../elements/common';
import { FormLabel } from '../../../../../elements/forms/FormLabel';
import { HorizontalFormGroupRead } from '../../../../../elements/forms/horizontalFormGroupRead/HorizontalFormGroupRead';
import { OsasQuestionnaireScoreIndicator } from './OsasQuestionnaireScoreIndicator';

export interface IOsasScoreProps {
    color?: string;
    label: string;
    value: number | string;
}

export function OsasV1QuestionnairesRead(props: ICustomControlProcedureReadProps & { showHeader?: boolean }) {
    const [expandedRows, setExpandedRows] = useState<string[]>([]);

    if (
        !props.procedure.customControlData?.questionnaires?.some((questionnaire: any) =>
            [OsasQuestionnaireName.fosq10, OsasQuestionnaireName.ess, OsasQuestionnaireName.hsdq].includes(
                questionnaire.name
            )
        )
    ) {
        return null;
    }

    let fosq10Items;
    let essItems;
    let hsdqItems;
    let fosq10Score;
    let essScore;
    let hsdqScore;

    const essQuestionnaire: IOsasQuestionnaire = props.procedure.customControlData.questionnaires.find(
        (questionnaire: any) => questionnaire.name === OsasQuestionnaireName.ess
    );
    const fosq10Questionnaire: IOsasQuestionnaire = props.procedure.customControlData.questionnaires.find(
        (questionnaire: any) => questionnaire.name === OsasQuestionnaireName.fosq10
    );
    const hsdqQuestionnaire: IOsasQuestionnaire = props.procedure.customControlData.questionnaires.find(
        (questionnaire: any) => questionnaire.name === OsasQuestionnaireName.hsdq
    );

    if (essQuestionnaire) {
        essItems = getEssItemsOnColumns(essQuestionnaire.answer);
        essScore = getOsasQuestionnaireScore(essQuestionnaire);
    }
    if (fosq10Questionnaire) {
        fosq10Items = getFosq10ItemsOnColumns(fosq10Questionnaire.answer);
        fosq10Score = getOsasQuestionnaireScore(fosq10Questionnaire);
    }
    if (hsdqQuestionnaire) {
        hsdqItems = getHsdqCategories(hsdqQuestionnaire.answer);
        hsdqScore = getOsasQuestionnaireScore(hsdqQuestionnaire);
    }

    function handleOnExpandRow(rowId: string) {
        const newExpandedRows = expandedRows.slice();
        const index = newExpandedRows.indexOf(rowId);
        if (index >= 0) {
            newExpandedRows.splice(index, 1);
        } else {
            newExpandedRows.push(rowId);
        }
        setExpandedRows(newExpandedRows);
    }

    return (
        <>
            {props.showHeader && (
                <>
                    <Divider />
                    <FormHeader>{`${hsdqQuestionnaire ? 'HSDQ, ' : ''}FOSQ-10 en ESS: 0-meting`}</FormHeader>
                </>
            )}
            <FormSection>
                {hsdqScore && (
                    <OsasQuestionnaireScoreIndicator
                        direction="right"
                        norm={getHsdqResultMessage(hsdqScore)}
                        osasQuestionnaireScoreType="hsdq"
                        score={Number.parseFloat(hsdqScore.toPrecision(3))}
                        variant="hsdqEdgedGradient"
                    />
                )}

                {fosq10Questionnaire && (
                    <OsasQuestionnaireScoreIndicator
                        direction="left"
                        osasQuestionnaireScoreType="fosq10"
                        score={fosq10Score}
                        variant="gradient"
                    />
                )}

                {essQuestionnaire && (
                    <OsasQuestionnaireScoreIndicator
                        direction="right"
                        norm={getEssResultMessage(essScore)}
                        osasQuestionnaireScoreType="ess"
                        score={essScore}
                        variant="edgedGradient"
                    />
                )}
            </FormSection>

            {hsdqQuestionnaire && (
                <FormSection title="HSDQ">
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableHeader></TableHeader>
                                <TableHeader width={25}></TableHeader>
                                <TableHeader className={'font-weight-normal'}>Score</TableHeader>
                                <TableHeader className={'font-weight-normal'}>Overschrijding</TableHeader>
                                <TableHeader
                                    className={'font-weight-normal'}
                                    color={'grey'}
                                    width={50}
                                >
                                    Criterium
                                </TableHeader>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {hsdqItems &&
                                hsdqItems.map((item) => (
                                    <Fragment key={item.name}>
                                        <TableRow
                                            key={item.name}
                                            style={{ border: 'hidden' }}
                                        >
                                            <TableCellExpandRow
                                                isExpandable
                                                isExpanded={expandedRows.includes(item.name)}
                                                onExpand={() => handleOnExpandRow(item.name)}
                                            />
                                            <TableCell className="pl-0">{item.name}</TableCell>
                                            <TableCell
                                                className="pl-0"
                                                style={{ fontWeight: 700 }}
                                            >
                                                {item.score.toPrecision(3)}
                                            </TableCell>
                                            <TableCell
                                                className="pl-0"
                                                style={{ fontWeight: 700 }}
                                            >
                                                {item.overrun}
                                            </TableCell>
                                            <TableCell
                                                className="pl-0"
                                                style={{ fontWeight: 700, color: 'grey' }}
                                            >
                                                {item.criteria.toPrecision(3)}
                                            </TableCell>
                                        </TableRow>
                                        {expandedRows.includes(item.name) && (
                                            <TableRow key={`${item.name}_expandable`}>
                                                <TableCell
                                                    colSpan={5}
                                                    style={{ paddingLeft: '62px' }}
                                                >
                                                    {getHsqdCategoryItems(item.name, hsdqQuestionnaire.answer)}
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </Fragment>
                                ))}
                        </TableBody>
                    </Table>
                </FormSection>
            )}

            {fosq10Questionnaire && (
                <FormSection title="FOSQ-10">
                    <Row>
                        <Col span={[12, 6]}>{fosq10Items?.leftColumnOfNumberedBadges}</Col>
                        <Col span={[12, 6]}>{fosq10Items?.rightColumnOfNumberedBadges}</Col>
                    </Row>
                </FormSection>
            )}

            {essQuestionnaire && (
                <FormSection title="ESS">
                    <Row>
                        <Col span={[12, 6]}>{essItems?.leftColumnOfNumberedBadges}</Col>
                        <Col span={[12, 6]}>{essItems?.rightColumnOfNumberedBadges}</Col>
                    </Row>
                </FormSection>
            )}
        </>
    );
}

function getFosq10ItemsOnColumns(fosq10Items: IFosq10OsasQuestionnaire) {
    const leftColumnOfNumberedBadges: JSX.Element[] = [];
    const rightColumnOfNumberedBadges: JSX.Element[] = [];
    const rightColumnOfNumberedBadgesAndNvtValue: JSX.Element[] = [];
    const orderedFosq10Items = Object.entries(fosq10Items).sort((a, b) => a[1] - b[1]) as [
        keyof DefaultLanguage['osasQuestionnaires'],
        number,
    ][];

    for (const [key, value] of orderedFosq10Items) {
        if (value === 1 || value === 2) {
            leftColumnOfNumberedBadges.push(
                <OsasScore
                    color={getColorForFosq10Value(value)}
                    label={getSelectedLanguage().osasQuestionnaires[key]}
                    value={value}
                />
            );
        } else if (value === 0) {
            rightColumnOfNumberedBadgesAndNvtValue.push(
                <OsasScore
                    color={getColorForFosq10Value(value)}
                    label={getSelectedLanguage().osasQuestionnaires[key]}
                    value="NVT"
                />
            );
        } else {
            rightColumnOfNumberedBadges.push(
                <OsasScore
                    color={getColorForFosq10Value(value)}
                    label={getSelectedLanguage().osasQuestionnaires[key]}
                    value={value}
                />
            );
        }
    }

    return {
        leftColumnOfNumberedBadges,
        rightColumnOfNumberedBadges: [...rightColumnOfNumberedBadges, ...rightColumnOfNumberedBadgesAndNvtValue],
    };
}

function getEssItemsOnColumns(essItems: IEssOsasQuestionnaire) {
    const leftColumnOfNumberedBadges: JSX.Element[] = [];
    const rightColumnOfNumberedBadges: JSX.Element[] = [];
    const orderedEssItems = Object.entries(essItems).sort((a, b) => b[1] - a[1]) as [
        keyof DefaultLanguage['osasQuestionnaires'],
        number,
    ][];

    for (const [key, value] of orderedEssItems) {
        if (value === 2 || value === 3) {
            leftColumnOfNumberedBadges.push(
                <OsasScore
                    color={getColorForEssValue(value)}
                    label={getSelectedLanguage().osasQuestionnaires[key]}
                    value={value}
                />
            );
        } else {
            rightColumnOfNumberedBadges.push(
                <OsasScore
                    color={getColorForEssValue(value)}
                    label={getSelectedLanguage().osasQuestionnaires[key]}
                    value={value}
                />
            );
        }
    }

    return { leftColumnOfNumberedBadges, rightColumnOfNumberedBadges };
}

function getColorForFosq10Value(value: number) {
    let color;
    switch (value) {
        case 1:
            color = OsasScoreColor.red;
            break;
        case 2:
            color = OsasScoreColor.orange;
            break;
        case 3:
            color = OsasScoreColor.yellow;
            break;
        case 4:
            color = OsasScoreColor.green;
            break;
        default:
            color = OsasScoreColor.gray;
            break;
    }

    return color;
}

function getColorForEssValue(value: number) {
    let color;
    switch (value) {
        case 0:
            color = OsasScoreColor.green;
            break;
        case 1:
            color = OsasScoreColor.yellow;
            break;
        case 2:
            color = OsasScoreColor.orange;
            break;
        case 3:
            color = OsasScoreColor.red;
            break;
        default:
            break;
    }

    return color;
}

function getHsqdCategoryItems(category: HsdqCategoryName, hsdqQuestionnaire: IHsdqOsasQuestionnaire) {
    const categoryItems = getHsdqQuestionsByCategory(category, hsdqQuestionnaire);

    const listItems = [];
    if (categoryItems) {
        for (let i = 5; i > 0; i--) {
            const questions = Object.keys(categoryItems)
                .filter((question) => categoryItems[question as keyof typeof categoryItems] === i)
                .map((question) => (
                    <List.Item
                        key={`${category}_${question}`}
                        style={{ fontWeight: 700 }}
                    >
                        {getSelectedLanguage().osasQuestionnaires[question as keyof typeof categoryItems]}
                    </List.Item>
                ));

            if (questions.length > 0) {
                listItems.push(
                    <HorizontalFormGroupRead key={`${category}_${i}`}>
                        <FormLabel>{getHsdqAnswerByValue(i, getLanguageCode())}</FormLabel>
                        <List
                            styleType="bullet"
                            sx={{ mb: 3 }}
                        >
                            {questions}
                        </List>
                    </HorizontalFormGroupRead>
                );
            }
        }
    }

    return listItems;
}

export interface IOsasScoreProps {
    color?: string;
    label: string;
    value: number | string;
}

export function OsasScore({ label, value, color }: IOsasScoreProps) {
    return (
        <StyledOsasScore>
            <StyledLabel size="sm">{label}</StyledLabel>
            <StyledScore $color={color}>
                <StyledScoreValue>{value}</StyledScoreValue>
            </StyledScore>
        </StyledOsasScore>
    );
}

const StyledOsasScore = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    margin-bottom: 8px;
    padding-right: 2rem;
`;

export const StyledScore = styled.div<{ $color?: string }>`
    min-width: 21px;
    width: auto;
    height: 21px;
    border-radius: 9999px;
    background-color: ${(props) => props.$color};
    text-align: center;
    display: inline-flex;
    align-items: center;
    justify-content: center;
`;

export const StyledScoreValue = styled.span`
    display: inline-block;
    line-height: 21px;
    color: #fff;
    font-weight: 500;
    font-size: 13px;
`;

const StyledLabel = styled(Text)`
    word-break: break-word;
`;
